/**
 * Générateur — Attendu 661 — Identifier, nommer et caractériser les angles
 * (lexique : nul, aigu, droit, obtus, plat, plein ; notations ABĈ, Â, xÔy ;
 * angles opposés par le sommet, adjacents, supplémentaires).
 *
 * Niveau : 6ème | Cluster : 32 | Domaine : Espace et géométrie
 *
 * 6 exercices / 6 formats :
 *   - nature_angle_qcm          : QCM 4 choix (6 natures × ~7 mesures × 2
 *                                  formulations) — reconnaître la nature d'un
 *                                  angle à partir de sa mesure.
 *   - lexique_angle_trou        : texte à trous (5 modèles) — compléter les
 *                                  définitions du lexique (mesures limites,
 *                                  notation ABĈ, supplémentaires...).
 *   - notation_angle_qcm_svg    : QCM 4 choix + figure SVG — choisir la bonne
 *                                  notation `ABĈ` pour l'angle marqué sur la
 *                                  figure.
 *   - opposes_adj_saisie        : saisie numérique + figure SVG — calculer un
 *                                  angle opposé par le sommet (régime 4a) ou
 *                                  adjacent supplémentaire (régime 4b).
 *   - config_angles_vf          : Vrai/Faux (3V+3F) + figure SVG — identifier
 *                                  opposés/adjacents/supplémentaires sur 2
 *                                  types de figures (2 droites sécantes ou
 *                                  3 demi-droites).
 *   - supplementaires_saisie    : saisie numérique (3 variantes) — calculer
 *                                  un angle supplémentaire sans figure.
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_661.md et docs/fiches/PAYLOADS_661.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 661;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE   = "Nature d'un angle";
const L_LEXIQUE  = 'Lexique des angles';
const L_NOTATION = "Notation d'un angle (figure)";
const L_OPP_ADJ  = 'Angles opposés/adjacents';
const L_CONFIG   = "Configurations d'angles";
const L_SUPP     = 'Angles supplémentaires';

// Lettres valides — exclut I (confusion avec 1) et O (réservé au sommet xÔy)
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

const COLOR_BLEU  = '#378ADD';
const COLOR_ROUGE = '#E24B4A';

const VIEWBOX_NOT = { w: 300, h: 200 }; // Ex. 3
const VIEWBOX_FIG = { w: 320, h: 240 }; // Ex. 4, 5

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Combining circumflex U+0302 — rend la lettre surmontée d'un chapeau. */
const chapeau = (c) => c + '\u0302';

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Sérialise une string SVG en data URL utf8. */
const svgToDataUrl = (svgString) =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;

/**
 * Construit l'attribut `d` d'un `<path>` pour un arc circulaire, de l'angle
 * `a1Deg` à `a2Deg` (en convention mathématique : 0° = est, 90° = nord),
 * centré en (cx, cy), de rayon `rayon`. `sweep = 0` dessine l'arc à l'intérieur
 * de l'angle a1→a2 (côté "saillant"). Le flag `largeArc` est calculé d'après
 * le delta angulaire normalisé.
 */
const arcD = (cx, cy, rayon, a1Deg, a2Deg) => {
    const a1 = (a1Deg * Math.PI) / 180;
    const a2 = (a2Deg * Math.PI) / 180;
    const x1 = cx + rayon * Math.cos(a1);
    const y1 = cy - rayon * Math.sin(a1);
    const x2 = cx + rayon * Math.cos(a2);
    const y2 = cy - rayon * Math.sin(a2);
    const deltaAbs = ((a2Deg - a1Deg) % 360 + 360) % 360;
    const largeArc = deltaAbs > 180 ? 1 : 0;
    const sweep = 0;
    return `M ${x1.toFixed(1)},${y1.toFixed(1)} A ${rayon} ${rayon} 0 ${largeArc} ${sweep} ${x2.toFixed(1)},${y2.toFixed(1)}`;
};

/**
 * Décale `pt` de `decal` px dans la direction opposée à `vers`. Utile pour
 * placer une étiquette juste au-delà d'un point, loin du sommet.
 */
const labelPosAt = (pt, vers, decal = 10) => {
    const dx = pt.x - vers.x;
    const dy = pt.y - vers.y;
    const n = Math.hypot(dx, dy);
    if (n < 1e-3) return { x: pt.x, y: pt.y };
    return { x: pt.x + (dx / n) * decal, y: pt.y + (dy / n) * decal };
};

/** Construit un `<text>` SVG simple (étiquette). */
const svgLabel = (text, x, y, { size = 13, italic = false, anchor = 'start', color = null } = {}) =>
    `<text x="${x.toFixed(0)}" y="${y.toFixed(0)}" font-family="serif" font-size="${size}"`
    + (italic ? ' font-style="italic"' : '')
    + (anchor !== 'start' ? ` text-anchor="${anchor}"` : '')
    + (color ? ` fill="${color}"` : '')
    + `>${text}</text>`;

/**
 * Ouvre un `<svg>` avec un attribut `data-v="{salt}"` placé AVANT `xmlns` pour
 * garantir que la variabilité tombe dans les 80 premiers caractères de
 * l'encodage utf8 (contrainte fingerprint auditGen, cf. `tests/auditGen.js`).
 */
const svgOpen = (vb, salt) =>
    `<svg data-v="${salt}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">`;

/** Salt numérique dérivé de paramètres variants (stable pour mêmes params). */
const makeSalt = (...nums) => nums.map(n => Math.round(n)).join('_');

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Nature d'un angle (QCM sans figure)
// ═══════════════════════════════════════════════════════════

const MESURES_EX1 = {
    nul:   [0],
    aigu:  [5, 12, 25, 35, 42, 55, 67, 78, 85, 89],
    droit: [90],
    obtus: [91, 100, 115, 127, 140, 155, 168, 175, 179],
    plat:  [180],
    plein: [360],
};

const DISTRACTEURS_EX1 = {
    nul:   ['aigu', 'droit', 'plat'],                   // 3 — tous utilisés
    aigu:  ['droit', 'obtus', 'plat', 'nul'],           // tirer 3 sur 4
    droit: ['aigu', 'obtus', 'plat', 'plein'],
    obtus: ['aigu', 'droit', 'plat', 'plein'],
    plat:  ['obtus', 'droit', 'plein', 'aigu'],
    plein: ['plat', 'droit', 'nul', 'obtus'],
};

const NATURES_EX1 = Object.keys(MESURES_EX1);

const genNatureAngleQcm = () => {
    const nat = randomChoice(NATURES_EX1);
    const m = randomChoice(MESURES_EX1[nat]);
    const variante = randomChoice(['E1', 'E2', 'E3', 'E4']);
    // Lettre aléatoire — présente dans toutes les variantes pour garantir ≥ 20
    // fingerprints uniques (variante × nature × mesure × lettre).
    const L = randomChoice(LETTRES);
    const L_HAT = chapeau(L);

    let texte;
    if (variante === 'E1') {
        texte = `<p>Un angle ${L_HAT} mesure ${m}°. C'est un angle …</p>`;
    } else if (variante === 'E2') {
        texte = `<p>Quelle est la nature d'un angle ${L_HAT} de mesure ${m}° ?</p>`;
    } else if (variante === 'E3') {
        texte = `<p>L'angle ${L_HAT} mesure ${m}°. Quelle est sa nature ?</p>`;
    } else {
        texte = `<p>Sachant que ${L_HAT} = ${m}°, quelle est la nature de l'angle ${L_HAT} ?</p>`;
    }

    const distracteurs = randomChoiceMultiple(DISTRACTEURS_EX1[nat], 3);
    const choix = [nat, ...distracteurs];

    const payload = makeQcm(choix, nat, texte, opts(L_NATURE));
    payload._meta.generatorKey = 'nature_angle_qcm';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Lexique des angles (texte à trous, mode drag)
// ═══════════════════════════════════════════════════════════

// Chaque modèle paramétré par des lettres tirées aléatoirement pour garantir
// ≥ 20/20 uniques (auditGen). Les lettres rendent chaque phrase distinguable
// même entre modèles de même ID, tout en restant pédagogiquement cohérente.
const MODELES_EX2 = [
    {
        id: 'A',
        arity: 3,
        build: (A, B, C) => ({
            phrase: `${chapeau(A)} est un angle droit, ${chapeau(B)} est un angle plat et ${chapeau(C)} est un angle plein. Alors ${chapeau(A)} = {{90}}°, ${chapeau(B)} = {{180}}° et ${chapeau(C)} = {{360}}°.`,
            distracteurs: ['45', '60', '120', '270'],
        }),
    },
    {
        id: 'B',
        arity: 2,
        build: (A, B) => ({
            phrase: `${chapeau(A)} est un angle aigu et ${chapeau(B)} est un angle obtus. Donc ${chapeau(A)} mesure entre {{0}}° et {{90}}°, tandis que ${chapeau(B)} mesure entre {{90}}° et {{180}}°.`,
            distracteurs: ['45', '60', '120', '270'],
        }),
    },
    {
        id: 'C',
        arity: 3,
        build: (A, B, C) => ({
            phrase: `Dans la notation ${A}${chapeau(B)}${C} d'un angle, le sommet est la lettre du {{milieu}}, c'est-à-dire {{${B}}}.`,
            distracteurs: ['côté', 'extrémité', A, C],
        }),
    },
    {
        id: 'D',
        arity: 2,
        build: (A, B) => ({
            phrase: `Les angles ${chapeau(A)} et ${chapeau(B)} sont {{supplémentaires}} si la somme de leurs mesures vaut {{180}}°.`,
            distracteurs: ['complémentaires', 'adjacents', '90', '360'],
        }),
    },
    {
        id: 'E',
        arity: 4,
        build: (A, B, C, D) => ({
            phrase: `Les droites (${A}${B}) et (${C}${D}) sécantes forment {{quatre}} angles saillants ; deux angles opposés par le sommet ont la {{même}} mesure.`,
            distracteurs: ['deux', 'trois', 'cinq', 'différente'],
        }),
    },
];

const genLexiqueAngleTrou = () => {
    const modele = randomChoice(MODELES_EX2);
    const letters = pickN(modele.arity);
    const built = modele.build(...letters);

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une consigne d'une ligne, la phrase à trous reste
    // le coeur du format.
    const texte = '<p>Complète la phrase en glissant les étiquettes.</p>';

    const payload = makeTexteTrou(built.phrase, texte, {
        ...opts(L_LEXIQUE),
        mode: 'drag',
        distracteurs: built.distracteurs,
        strict: false,
    });
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: modele.id };
    payload._meta.generatorKey = 'lexique_angle_trou';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Notation d'un angle sur figure (QCM + SVG)
// ═══════════════════════════════════════════════════════════

const genNotationAngleQcmSvg = () => {
    const [P1_name, P2_name, P3_name] = pickN(3);

    // Placement géométrique — sommet au centre du viewBox, orientation aléatoire.
    const P2 = { x: randInt(135, 170), y: randInt(90, 115) };
    const rotationDeg = Math.random() * 360;
    const halfAngleDeg = randInt(25, 75); // demi-angle entre les 2 demi-droites
    const len1 = randInt(70, 100);
    const len3 = randInt(70, 100);

    const dir1Deg = rotationDeg - halfAngleDeg;
    const dir3Deg = rotationDeg + halfAngleDeg;
    const toRad = (d) => (d * Math.PI) / 180;

    // Conversion SVG y-flipped (math y positif = écran y négatif)
    const rawP1 = {
        x: P2.x + len1 * Math.cos(toRad(dir1Deg)),
        y: P2.y - len1 * Math.sin(toRad(dir1Deg)),
    };
    const rawP3 = {
        x: P2.x + len3 * Math.cos(toRad(dir3Deg)),
        y: P2.y - len3 * Math.sin(toRad(dir3Deg)),
    };

    // Clamp dans viewBox avec marge pour l'étiquette (+15 px)
    const clamp = (p) => ({
        x: Math.max(22, Math.min(VIEWBOX_NOT.w - 22, p.x)),
        y: Math.max(22, Math.min(VIEWBOX_NOT.h - 22, p.y)),
    });
    const P1 = clamp(rawP1);
    const P3 = clamp(rawP3);

    // Recalcul des angles effectifs après clamp (pour que l'arc soit au bon endroit).
    const angEff1 = Math.atan2(-(P1.y - P2.y), P1.x - P2.x) * 180 / Math.PI;
    const angEff3 = Math.atan2(-(P3.y - P2.y), P3.x - P2.x) * 180 / Math.PI;
    // Arc court entre les 2 directions (petit côté).
    let arcStart = angEff1, arcEnd = angEff3;
    const normDelta = ((angEff3 - angEff1) % 360 + 360) % 360;
    if (normDelta > 180) { arcStart = angEff3; arcEnd = angEff1; }

    // Étiquettes décalées vers l'extérieur
    const lbl1 = labelPosAt(P1, P2, 10);
    const lbl3 = labelPosAt(P3, P2, 10);
    const midOpp = { x: (P1.x + P3.x) / 2, y: (P1.y + P3.y) / 2 };
    const lbl2 = labelPosAt(P2, midOpp, 12);

    const salt = makeSalt(rotationDeg, halfAngleDeg);
    const svg = svgOpen(VIEWBOX_NOT, salt)
        + `<line x1="${P2.x.toFixed(1)}" y1="${P2.y.toFixed(1)}" x2="${P1.x.toFixed(1)}" y2="${P1.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${P2.x.toFixed(1)}" y1="${P2.y.toFixed(1)}" x2="${P3.x.toFixed(1)}" y2="${P3.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(P2.x, P2.y, 18, arcStart, arcEnd)}" fill="none" stroke="${COLOR_ROUGE}" stroke-width="2"/>`
        + `<circle cx="${P2.x.toFixed(1)}" cy="${P2.y.toFixed(1)}" r="2" fill="black"/>`
        + `<circle cx="${P1.x.toFixed(1)}" cy="${P1.y.toFixed(1)}" r="2" fill="black"/>`
        + `<circle cx="${P3.x.toFixed(1)}" cy="${P3.y.toFixed(1)}" r="2" fill="black"/>`
        + svgLabel(P1_name, lbl1.x, lbl1.y)
        + svgLabel(P2_name, lbl2.x, lbl2.y)
        + svgLabel(P3_name, lbl3.x, lbl3.y)
        + `</svg>`;
    const imageData = svgToDataUrl(svg);

    // Choix en KaTeX (PAS de chapeau Unicode combinant) : normalizeAnswer du
    // scoring supprime les diacritiques, donc "PK̂H" et "PKĤ" devenaient
    // équivalents → le distracteur était accepté comme bonne réponse. En KaTeX,
    // les chaînes restent distinctes au scoring et le rendu est identique.
    const correct = `$${P1_name}\\hat{${P2_name}}${P3_name}$`;
    const distracteursPool = [
        `$\\hat{${P1_name}}${P2_name}${P3_name}$`,  // chapeau sur extrémité
        `$${P1_name}${P2_name}\\hat{${P3_name}}$`,  // chapeau sur l'autre extrémité
        `$${P1_name}${P2_name}${P3_name}$`,         // oubli du chapeau
        `$\\hat{${P2_name}}$`,                      // notation 1 lettre (ambiguë)
        `$${P1_name}${P3_name}$`,                   // juste un segment
    ];
    const distracteurs = randomChoiceMultiple(distracteursPool, 3);
    const choix = [correct, ...distracteurs];

    const texte = `<p>Comment note-t-on l'angle coloré sur la figure ?</p>`;

    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_NOTATION),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'notation_angle_qcm_svg';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Angles opposés par le sommet / adjacents (saisie + SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX4 = [25, 35, 42, 55, 67, 78, 110, 125, 140, 155];

/** SVG figure pour Ex. 4 : 2 droites sécantes en O, arc sur xÔy en bleu. */
const buildFigureEx4Svg = (m, theta1Deg, extraSalt = 0) => {
    const Ox = 160, Oy = 120, len = 90;
    const toRad = (d) => (d * Math.PI) / 180;

    const theta2Deg = theta1Deg + m;
    const dirs = {
        x: theta1Deg,
        y: theta2Deg,
        z: theta1Deg + 180,
        t: theta2Deg + 180,
    };
    const ptAt = (angDeg) => ({
        x: Ox + len * Math.cos(toRad(angDeg)),
        y: Oy - len * Math.sin(toRad(angDeg)),
    });
    const X = ptAt(dirs.x);
    const Y = ptAt(dirs.y);
    const Z = ptAt(dirs.z);
    const T = ptAt(dirs.t);

    // Texte de la mesure à l'intérieur de l'arc
    const angMoy = (dirs.x + dirs.y) / 2;
    const txtPos = {
        x: Ox + 30 * Math.cos(toRad(angMoy)),
        y: Oy - 30 * Math.sin(toRad(angMoy)),
    };

    const lblX = labelPosAt(X, { x: Ox, y: Oy }, 12);
    const lblY = labelPosAt(Y, { x: Ox, y: Oy }, 12);
    const lblZ = labelPosAt(Z, { x: Ox, y: Oy }, 12);
    const lblT = labelPosAt(T, { x: Ox, y: Oy }, 12);

    // L'étiquette O est posée À L'OPPOSÉ de l'arc, et non à une position fixe :
    // quand la bissectrice pointait vers le bas-droite, la mesure venait se
    // superposer au O (mesuré à 4,2 % des tirages sur l'Ex. 5).
    const posO = {
        x: Ox + 16 * Math.cos(toRad(angMoy + 180)),
        y: Oy - 16 * Math.sin(toRad(angMoy + 180)) + 4,
    };

    const salt = makeSalt(theta1Deg, m, extraSalt);
    return svgOpen(VIEWBOX_FIG, salt)
        + `<line x1="${X.x.toFixed(1)}" y1="${X.y.toFixed(1)}" x2="${Z.x.toFixed(1)}" y2="${Z.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${Y.x.toFixed(1)}" y1="${Y.y.toFixed(1)}" x2="${T.x.toFixed(1)}" y2="${T.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(Ox, Oy, 18, dirs.x, dirs.y)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`
        + svgLabel(`${m}°`, txtPos.x, txtPos.y, { size: 11, color: COLOR_BLEU, anchor: 'middle' })
        + `<circle cx="${Ox}" cy="${Oy}" r="2.5" fill="black"/>`
        + svgLabel('x', lblX.x, lblX.y, { italic: true })
        + svgLabel('y', lblY.x, lblY.y, { italic: true })
        + svgLabel('z', lblZ.x, lblZ.y, { italic: true })
        + svgLabel('t', lblT.x, lblT.y, { italic: true })
        + svgLabel('O', posO.x, posO.y)
        + `</svg>`;
};

const genOpposesAdjSaisie = () => {
    const regime = randomChoice(['4a', '4b']);
    const m = randomChoice(MESURES_EX4);
    const theta1Deg = Math.random() * 180;
    // Salt additionnel : entier 6 chiffres → garantit des fingerprints uniques
    // au-delà du seul couple (m, theta1 arrondi) qui n'offre que ~3600 combos.
    const uniqSalt = randInt(100000, 999999);

    const svg = buildFigureEx4Svg(m, theta1Deg, uniqSalt);
    const imageData = svgToDataUrl(svg);

    const O_HAT = chapeau('O');
    const reponse = regime === '4a' ? m : 180 - m;
    const texte = regime === '4a'
        ? `<p>Sur la figure, x${O_HAT}y = ${m}°. Combien vaut l'angle z${O_HAT}t (opposé par le sommet) ?</p>`
        : `<p>Sur la figure, x${O_HAT}y = ${m}°. Combien vaut l'angle y${O_HAT}z (adjacent à x${O_HAT}y sur la droite (xz)) ?</p>`;

    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_OPP_ADJ),
        strict: true,
        suffixe: '°',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    payload._meta.generatorKey = 'opposes_adj_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Configurations d'angles (V/F + SVG)
// ═══════════════════════════════════════════════════════════

const MESURES_EX5_A = [30, 45, 60, 75, 105, 120, 135, 150]; // évite 90°
const MESURES_EX5_B = [20, 30, 40, 50, 60, 70, 80];

const O_HAT = chapeau('O');
const xOy = `x${O_HAT}y`;
const yOz = `y${O_HAT}z`;
const zOt = `z${O_HAT}t`;
const tOx = `t${O_HAT}x`;
const xOz = `x${O_HAT}z`;

/** SVG figure pour Ex. 5 Type A (identique à Ex. 4 : 2 droites sécantes). */
const buildFigureEx5A = (m, theta1Deg) => buildFigureEx4Svg(m, theta1Deg);

/** SVG figure pour Ex. 5 Type B : 3 demi-droites de même origine O. */
const buildFigureEx5B = (m1, m2, thetaXDeg) => {
    const Ox = 160, Oy = 120, len = 90;
    const toRad = (d) => (d * Math.PI) / 180;

    const dirs = {
        x: thetaXDeg,
        y: thetaXDeg + m1,
        z: thetaXDeg + m1 + m2,
    };
    const ptAt = (angDeg) => ({
        x: Ox + len * Math.cos(toRad(angDeg)),
        y: Oy - len * Math.sin(toRad(angDeg)),
    });
    const X = ptAt(dirs.x);
    const Y = ptAt(dirs.y);
    const Z = ptAt(dirs.z);

    // Textes des mesures à l'intérieur de chaque arc (radius 30)
    const angMoy1 = (dirs.x + dirs.y) / 2;
    const angMoy2 = (dirs.y + dirs.z) / 2;
    // Rayons DIFFÉRENTS pour les deux mesures : à m1 = m2 = 20° les deux
    // bissectrices ne sont écartées que de 20°, soit 11 px à rayon égal.
    const txt1 = {
        x: Ox + 30 * Math.cos(toRad(angMoy1)),
        y: Oy - 30 * Math.sin(toRad(angMoy1)),
    };
    const txt2 = {
        x: Ox + 46 * Math.cos(toRad(angMoy2)),
        y: Oy - 46 * Math.sin(toRad(angMoy2)),
    };
    // O à l'opposé de la zone marquée (bissectrice de x→z).
    const posO = {
        x: Ox + 16 * Math.cos(toRad((dirs.x + dirs.z) / 2 + 180)),
        y: Oy - 16 * Math.sin(toRad((dirs.x + dirs.z) / 2 + 180)) + 4,
    };

    const lblX = labelPosAt(X, { x: Ox, y: Oy }, 12);
    const lblY = labelPosAt(Y, { x: Ox, y: Oy }, 12);
    const lblZ = labelPosAt(Z, { x: Ox, y: Oy }, 12);

    const salt = makeSalt(thetaXDeg, m1, m2);
    return svgOpen(VIEWBOX_FIG, salt)
        + `<line x1="${Ox}" y1="${Oy}" x2="${X.x.toFixed(1)}" y2="${X.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${Ox}" y1="${Oy}" x2="${Y.x.toFixed(1)}" y2="${Y.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<line x1="${Ox}" y1="${Oy}" x2="${Z.x.toFixed(1)}" y2="${Z.y.toFixed(1)}" stroke="black" stroke-width="1.5"/>`
        + `<path d="${arcD(Ox, Oy, 16, dirs.x, dirs.y)}" fill="none" stroke="${COLOR_BLEU}" stroke-width="2"/>`
        + `<path d="${arcD(Ox, Oy, 22, dirs.y, dirs.z)}" fill="none" stroke="${COLOR_ROUGE}" stroke-width="2"/>`
        + svgLabel(`${m1}°`, txt1.x, txt1.y, { size: 10, color: COLOR_BLEU, anchor: 'middle' })
        + svgLabel(`${m2}°`, txt2.x, txt2.y, { size: 10, color: COLOR_ROUGE, anchor: 'middle' })
        + `<circle cx="${Ox}" cy="${Oy}" r="2.5" fill="black"/>`
        + svgLabel('x', lblX.x, lblX.y, { italic: true })
        + svgLabel('y', lblY.x, lblY.y, { italic: true })
        + svgLabel('z', lblZ.x, lblZ.y, { italic: true })
        + svgLabel('O', posO.x, posO.y)
        + `</svg>`;
};

const genConfigAnglesVfA = () => {
    const m = randomChoice(MESURES_EX5_A);
    const theta1Deg = Math.random() * 180;

    const svg = buildFigureEx5A(m, theta1Deg);
    const imageData = svgToDataUrl(svg);

    // Pool VRAIES (6 candidats — V6 seulement si m<90°)
    const poolV = [
        `${zOt} = ${m}°`,
        `${xOy} et ${yOz} sont des angles adjacents.`,
        `${xOy} et ${yOz} sont supplémentaires.`,
        `La somme ${xOy} + ${yOz} + ${zOt} + ${tOx} vaut 360°.`,
        `${xOy} et ${zOt} sont opposés par le sommet.`,
    ];
    if (m < 90) {
        poolV.push(`Si ${m}° < 90° alors ${xOy} est un angle aigu.`);
    }
    // Pool FAUSSES (5 candidats)
    const poolF = [
        `${xOy} et ${zOt} sont des angles adjacents.`,
        `${xOy} et ${yOz} sont opposés par le sommet.`,
        `${xOy} + ${zOt} = 180°.`,
        `Les angles ${xOy}, ${yOz}, ${zOt}, ${tOx} sont tous égaux.`,
        `L'angle ${xOy} est nul.`,
    ];

    const chosenV = randomChoiceMultiple(poolV, 3);
    const chosenF = randomChoiceMultiple(poolF, 3);

    // Shuffle côté générateur (makeVraiFaux ne mélange pas les lignes)
    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // Pas de « pour chaque affirmation, indique si elle est vraie ou fausse » :
    // le tableau Vrai/Faux le dit déjà. Seule reste l'information utile.
    const texte = `<p>Sur la figure ci-dessous, deux droites se coupent au point O et l'angle ${xOy} mesure ${m}°.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_CONFIG),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: 'secantes' };
    payload._meta.generatorKey = 'config_angles_vf';
    return payload;
};

const genConfigAnglesVfB = () => {
    // Tirer (m1, m2) avec m1+m2 ≤ 150
    let m1, m2;
    do {
        m1 = randomChoice(MESURES_EX5_B);
        m2 = randomChoice(MESURES_EX5_B);
    } while (m1 + m2 > 150);

    const thetaXDeg = Math.random() * 360;

    const svg = buildFigureEx5B(m1, m2, thetaXDeg);
    const imageData = svgToDataUrl(svg);

    // Pool VRAIES (6 candidats — V5 garantie ici car m1 ≤ 80 < 90°)
    const poolV = [
        `${xOy} et ${yOz} sont adjacents.`,
        `${xOz} = (${m1}+${m2})° = ${m1 + m2}°.`,
        // ⚠️ pas de '[' brut en ligne de tableau V/F (crochet orphelin → casse
        // parseComplexAnswer, réponse juste comptée fausse) → $\lbrack$
        `$\\lbrack Oy)$ est un côté commun aux angles ${xOy} et ${yOz}.`,
        `Le sommet commun à ${xOy} et ${yOz} est O.`,
        `Si ${m1}° < 90° alors ${xOy} est un angle aigu.`,
        `${xOy}, ${yOz} et ${xOz} ont le même sommet.`,
    ];
    // Pool FAUSSES (5 candidats, tous garantis FAUX car m1+m2 ≤ 150 < 180)
    const poolF = [
        `${xOy} et ${yOz} sont opposés par le sommet.`,
        `${xOy} et ${xOz} sont adjacents.`,
        `${xOy} et ${yOz} sont toujours supplémentaires.`,
        `${xOz} = ${xOy} (les deux angles sont égaux).`,
        `${xOy} et ${yOz} n'ont pas de sommet commun.`,
    ];

    const chosenV = randomChoiceMultiple(poolV, 3);
    const chosenF = randomChoiceMultiple(poolF, 3);

    const merged = shuffle([
        ...chosenV.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenF.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    const texte =
        `<p>Sur la figure ci-dessous, trois demi-droites [Ox), [Oy) et [Oz) partent du point O.`
        + ` On sait que ${xOy} = ${m1}° et ${yOz} = ${m2}°.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_CONFIG),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageData } }],
        },
    });
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: 'adjacents' };
    payload._meta.generatorKey = 'config_angles_vf';
    return payload;
};

const genConfigAnglesVf = () => {
    return randomChoice(['A', 'B']) === 'A'
        ? genConfigAnglesVfA()
        : genConfigAnglesVfB();
};

// ═══════════════════════════════════════════════════════════
// Ex. 6 — Angles supplémentaires (saisie numérique sans figure)
// ═══════════════════════════════════════════════════════════

const MESURES_SUPP = [15, 25, 35, 45, 55, 65, 72, 88, 95, 103, 110, 127, 135, 145, 155, 168, 175, 178];


const genSupplementairesSaisie = () => {
    const variante = randomChoice(['V1', 'V2', 'V3', 'V4', 'V5']);
    const x = randomChoice(MESURES_SUPP);
    const reponse = 180 - x;

    let texte;
    if (variante === 'V3') {
        const [A, B, C, D] = pickN(4);
        const ABC = A + chapeau(B) + C;
        const CBD = C + chapeau(B) + D;
        texte = `<p>${ABC} et ${CBD} sont deux angles supplémentaires adjacents. Sachant que ${ABC} = ${x}°, combien vaut ${CBD} ?</p>`;
    } else {
        // Lettres MAJUSCULES : la notation d'un angle par une seule lettre
        // s'écrit avec la majuscule du sommet. Les minuscules chapeautées
        // (â, b̂…) ne sont pas la convention.
        const [a, b] = pickN(2);
        const A_HAT = chapeau(a);
        const B_HAT = chapeau(b);
        if (variante === 'V1') {
            texte = `<p>${A_HAT} et ${B_HAT} sont deux angles supplémentaires. Calcule la mesure de ${B_HAT} si ${A_HAT} = ${x}°.</p>`;
        } else if (variante === 'V2') {
            texte = `<p>Deux angles ${A_HAT} et ${B_HAT} sont supplémentaires. On sait que ${A_HAT} = ${x}°. Combien vaut ${B_HAT} ?</p>`;
        } else if (variante === 'V4') {
            texte = `<p>L'angle ${A_HAT} est supplémentaire à l'angle ${B_HAT}. Sachant que ${A_HAT} = ${x}°, que vaut ${B_HAT} ?</p>`;
        } else {
            texte = `<p>Deux angles supplémentaires ${A_HAT} et ${B_HAT} sont tels que ${A_HAT} mesure ${x}°. Quelle est la mesure de ${B_HAT} ?</p>`;
        }
    }

    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_SUPP),
        strict: true,
        suffixe: '°',
    });
    payload._meta.generatorKey = 'supplementaires_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    nature_angle_qcm:        genNatureAngleQcm,
    lexique_angle_trou:      genLexiqueAngleTrou,
    notation_angle_qcm_svg:  genNotationAngleQcmSvg,
    opposes_adj_saisie:      genOpposesAdjSaisie,
    config_angles_vf:        genConfigAnglesVf,
    supplementaires_saisie:  genSupplementairesSaisie,
};

export const CATEGORIES = [
    { id: 'nature_angle',       label: L_NATURE,   formatsList: [{ key: 'nature_angle_qcm',       label: 'QCM' }] },
    { id: 'lexique_angle',      label: L_LEXIQUE,  formatsList: [{ key: 'lexique_angle_trou',     label: 'Texte à trous' }] },
    { id: 'notation_angle',     label: L_NOTATION, formatsList: [{ key: 'notation_angle_qcm_svg', label: 'QCM + figure' }] },
    { id: 'opposes_adjacents',  label: L_OPP_ADJ,  formatsList: [{ key: 'opposes_adj_saisie',     label: 'Saisie + figure' }] },
    { id: 'config_angles',      label: L_CONFIG,   formatsList: [{ key: 'config_angles_vf',       label: 'Vrai/Faux + figure' }] },
    { id: 'supplementaires',    label: L_SUPP,     formatsList: [{ key: 'supplementaires_saisie', label: 'Saisie' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genNatureAngleQcm,
    genLexiqueAngleTrou,
    genNotationAngleQcmSvg,
    genOpposesAdjSaisie,
    genConfigAnglesVf,
    genConfigAnglesVfA,
    genConfigAnglesVfB,
    genSupplementairesSaisie,
};
